import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import useContextMenu from 'src/hooks/useContextMenu';
import useUpdateStore from 'src/hooks/useUpdateStore';
import useUI from 'src/hooks/useUI';
import { EVENT_TYPES } from 'src/interfaces/enums';
import ApplicationService from 'src/services/ApplicationService';
import { UIActionTypes } from 'src/store/reducers/ui';

function useSyncStoreEvents() {
  const {
    updatePauseInStore,
    updateFullscreenInStore,
    updateReadyInStore,
    updatePlaybackRateInStore,
    updatePlaylistInStore,
    updateMarkersInStore,
    updateHasNativeHLSInStore,
    updateActiveSubtitleInStore,
    updateSubtitlesInStore,
    updatePictureInPictureStatusInStore,
    updateUserActiveInStore,
    updateVolumeInStore,
    updateSeekingInStore,
    updateVideoDimensionsInStore,
    updateBufferIndicatorInStore,
    updateVideoPlaybackQualityInStore,
    updateMediaInfo,
    updateBufferedInStore,
  } = useUpdateStore();

  const { setUserActive } = useUI();

  const dispatch = useDispatch();
  const { showContextMenu } = useContextMenu();

  useEffect(() => {
    const bodyEl = document.querySelector('body');
    if (!bodyEl) return;

    const mouseleaveCallback = () => {
      dispatch({ type: UIActionTypes.UNSET_MOUSE_ON_PLAYER });
      setUserActive(false);
    };

    const mouseenterCallback = () => dispatch({ type: UIActionTypes.SET_MOUSE_ON_PLAYER });

    bodyEl.addEventListener('mouseenter', mouseenterCallback);
    bodyEl.addEventListener('mouseleave', mouseleaveCallback);

    return () => {
      bodyEl.removeEventListener('mouseenter', mouseenterCallback);
      bodyEl.removeEventListener('mouseleave', mouseleaveCallback);
    };
  }, [dispatch, setUserActive]);

  const createSyncStoreListeners = useCallback(() => {
    const player = ApplicationService.player;
    if (!player) return;

    player.addEventListener(EVENT_TYPES.PLAY, () => updatePauseInStore(false));

    document.addEventListener('fullscreenchange', () => {
      updateFullscreenInStore();
    });

    player.addEventListener(EVENT_TYPES.RATE_CHANGE, () => {
      updatePlaybackRateInStore();
    });
    player.addEventListener(EVENT_TYPES.ENTERPICTUREINPICTURE, () => {
      updatePictureInPictureStatusInStore();
    });
    player.addEventListener(EVENT_TYPES.LEAVEPICTUREINPICTURE, () => {
      updatePictureInPictureStatusInStore();
    });
    player.addEventListener(EVENT_TYPES.SUBTITLE_CHANGE, updateActiveSubtitleInStore);
    player.addEventListener(EVENT_TYPES.CONTEXT_MENU, showContextMenu);

    player.addEventListener(EVENT_TYPES.PAUSE, () => updatePauseInStore(true));
    player.addEventListener(EVENT_TYPES.LOADED_METADATA, updatePlaylistInStore);
    player.addEventListener(EVENT_TYPES.LOADED_METADATA, updateHasNativeHLSInStore);
    player.addSubtitleListener(updateSubtitlesInStore);
    player.addEventListener(EVENT_TYPES.READY, updateReadyInStore);
    player.addEventListener(EVENT_TYPES.LOADED_METADATA, updateMarkersInStore);
    player.addEventListener(EVENT_TYPES.TIME_UPDATE, updateVideoPlaybackQualityInStore);
    player.addEventListener(EVENT_TYPES.PROGRESS, updateBufferIndicatorInStore);
    player.addEventListener(EVENT_TYPES.PROGRESS, updateBufferedInStore);
    player.addEventListener(EVENT_TYPES.RESOLUTION_CHANGE, updateMediaInfo);

    player.addEventListener(EVENT_TYPES.USER_ACTIVE, () => {
      updateUserActiveInStore(true);
    });

    player.addEventListener(EVENT_TYPES.USER_INACTIVE, () => {
      updateUserActiveInStore(false);
    });

    player.addEventListener(EVENT_TYPES.LOADED_METADATA, () => {
      updateSubtitlesInStore();
      updateVideoDimensionsInStore(player.videoDimensions);
      updateMediaInfo();
    });

    player.addEventListener(EVENT_TYPES.SEEK_INIT, () => {
      updateSeekingInStore(true);
    });

    player.addEventListener(EVENT_TYPES.SEEK_ENDED, () => {
      updateSeekingInStore(false);
    });

    player.addEventListener(EVENT_TYPES.VOLUME_CHANGE, () => {
      updateVolumeInStore();
    });
  }, []);

  return { createSyncStoreListeners };
}

export default useSyncStoreEvents;
